import React, { useMemo } from 'react';

/**
 * Psychrometric Chart Component — Auto-zoomed to data points
 * Cooling process: horizontal (constant W) to saturation curve, then follows
 * the saturation curve down to the leaving temperature.
 */

const CHART_W = 800;
const CHART_H = 500;
const MARGIN = { top: 30, right: 85, bottom: 55, left: 65 };
const PLOT_W = CHART_W - MARGIN.left - MARGIN.right;
const PLOT_H = CHART_H - MARGIN.top - MARGIN.bottom;

function satPressureKpa(tc) {
  if (tc >= 0) return 0.61121 * Math.exp((18.678 - tc / 234.5) * (tc / (257.14 + tc)));
  return 0.61115 * Math.exp((23.036 - tc / 333.7) * (tc / (279.82 + tc)));
}

function wSat(dbF, pAtm = 101.325) {
  const tc = (dbF - 32) * 5 / 9;
  const pws = satPressureKpa(tc);
  if (pws >= pAtm) return 0.2;
  return 0.62198 * pws / (pAtm - pws);
}

function wFromDbRh(dbF, rh, pAtm = 101.325) {
  return wSat(dbF, pAtm) * rh / 100;
}

function dbAtSatW(targetW, pAtm = 101.325) {
  let lo = 30, hi = 120;
  for (let i = 0; i < 50; i++) {
    const mid = (lo + hi) / 2;
    if (wSat(mid, pAtm) < targetW) lo = mid; else hi = mid;
  }
  return (lo + hi) / 2;
}

// RH at given (DBT °F, humidity ratio lb/lb). Inverts W = 0.62198·pw/(pAtm-pw)
// to get pw, then pw/pws(T) × 100. Clamped to [0, 100] for label stability
// (numerical noise on the saturation curve can produce 100.3 %).
function rhFromDbW(dbF, w, pAtm = 101.325) {
  const tc = (dbF - 32) * 5 / 9;
  const pws = satPressureKpa(tc);
  if (pws <= 0 || w <= 0) return 0;
  const pw = (w * pAtm) / (0.62198 + w);
  const rh = (pw / pws) * 100;
  return Math.max(0, Math.min(100, rh));
}

// Display-unit helpers: internal math stays in IP, only the rendered numbers
// and axis labels convert when isSI is true.
const F_TO_C = (f) => (f - 32) * 5 / 9;
const GR_TO_GKG = (gr) => gr * (1000 / 7000); // 1 grain/lb = 0.14286 g/kg
const BTUH_TO_W = (b) => b * 0.29307107;
const CFM_TO_LS = (cfm) => cfm * 0.47194745;

function fmtT(f, isSI, d = 0) {
  if (f == null) return "—";
  const v = isSI ? F_TO_C(f) : f;
  return d ? v.toFixed(d) : Math.round(v);
}
function fmtW(grains, isSI) {
  if (grains == null) return "—";
  const v = isSI ? GR_TO_GKG(grains) : grains;
  return isSI ? v.toFixed(1) : Math.round(v);
}
function fmtCap(btuh, isSI) {
  if (btuh == null || Number.isNaN(btuh)) return "—";
  const v = isSI ? BTUH_TO_W(btuh) : btuh;
  return isSI ? `${Math.round(v).toLocaleString()} W` : `${Math.round(v).toLocaleString()} BTU/hr`;
}
function fmtFlow(cfm, isSI) {
  if (cfm == null) return "—";
  return isSI
    ? `${CFM_TO_LS(cfm).toFixed(1)} L/s`
    : `${Math.round(cfm).toLocaleString()} CFM`;
}

function computeBounds(points) {
  const allDb = Object.values(points).map(p => p.db_f);
  // Convert w_grains to w (lb/lb) - divide by 7000
  const allW = Object.values(points).map(p => p.w !== undefined ? p.w : (p.w_grains / 7000));
  const dataDbMin = Math.min(...allDb);
  const dataDbMax = Math.max(...allDb);
  const dataWMax = Math.max(...allW);

  // Fixed-width zoom (per user spec): every chart shows roughly the same
  // window so the user can compare rooms at a glance without recalibrating.
  // Padding ≈ 5 °C on each side of the data cluster (9 °F), and the y-axis
  // is stretched to the saturation humidity at dbMax so the full sat curve
  // is always visible — matches screenshot reference (10-27 °C, 0-22.9 g/kg).
  const dbPad = 9; // °F  (= 5 °C)
  const stepDB = 5;
  let dbMin = Math.floor((dataDbMin - dbPad) / stepDB) * stepDB;
  let dbMax = Math.ceil((dataDbMax + dbPad) / stepDB) * stepDB;
  // Enforce at least a 30 °F (~17 °C) visible window so the chart never
  // collapses around a tight cluster.
  if (dbMax - dbMin < 30) {
    const mid = (dbMin + dbMax) / 2;
    dbMin = Math.floor((mid - 15) / stepDB) * stepDB;
    dbMax = Math.ceil((mid + 15) / stepDB) * stepDB;
  }
  dbMin = Math.max(30, dbMin);
  dbMax = Math.min(120, dbMax);

  // y-axis: 0 to the saturation humidity at the right edge (rounded up
  // to a tidy grain step), ensuring the saturation curve climbs into view.
  let wMin = 0;
  const wSatAtMax = wSat(dbMax);
  const wHeadroom = Math.max(wSatAtMax, dataWMax * 1.25);
  // Round up to a 10-grain step for tidy y-tick labels.
  let wMax = Math.ceil(wHeadroom * 7000 / 10) * 10 / 7000;
  if (wMax < 0.006) wMax = 0.006;

  return { dbMin, dbMax, wMin, wMax };
}

function makeXScale(dbMin, dbMax) {
  return (dbF) => MARGIN.left + ((dbF - dbMin) / (dbMax - dbMin)) * PLOT_W;
}
function makeYScale(wMin, wMax) {
  return (w) => MARGIN.top + PLOT_H - ((w - wMin) / (wMax - wMin)) * PLOT_H;
}

function genSatCurve(xS, yS, dbMin, dbMax, wMax) {
  const pts = [];
  for (let db = dbMin; db <= dbMax; db += 0.5) {
    const ws = wSat(db);
    if (ws <= wMax * 1.05) pts.push({ x: xS(db), y: yS(Math.min(ws, wMax)) });
  }
  return pts;
}

function genRhCurve(rh, xS, yS, dbMin, dbMax, wMax) {
  const pts = [];
  for (let db = dbMin; db <= dbMax; db += 1) {
    const w = wFromDbRh(db, rh);
    if (w <= wMax && w >= 0) pts.push({ x: xS(db), y: yS(w) });
  }
  return pts;
}

function pointsToPath(pts) {
  if (!pts.length) return '';
  return 'M ' + pts.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' L ');
}

function buildCoolingPath(fromDbF, fromW, toDbF, xS, yS, wMax) {
  const segments = [];
  const satDb = dbAtSatW(fromW);

  if (fromDbF <= satDb + 0.5) {
    return buildSatCurvePath(fromDbF, toDbF, xS, yS, wMax);
  }

  segments.push({
    type: 'line',
    x1: xS(fromDbF), y1: yS(fromW),
    x2: xS(satDb), y2: yS(fromW),
  });

  const curvePoints = [];
  const step = -0.5;
  for (let db = satDb; db >= toDbF; db += step) {
    const ws = wSat(db);
    curvePoints.push({ x: xS(db), y: yS(Math.min(ws, wMax)) });
  }
  curvePoints.push({ x: xS(toDbF), y: yS(Math.min(wSat(toDbF), wMax)) });

  if (curvePoints.length > 1) segments.push({ type: 'curve', points: curvePoints });
  return segments;
}

function buildSatCurvePath(fromDbF, toDbF, xS, yS, wMax) {
  const pts = [];
  const step = fromDbF > toDbF ? -0.5 : 0.5;
  for (let db = fromDbF; step < 0 ? db >= toDbF : db <= toDbF; db += step) {
    const ws = wSat(db);
    pts.push({ x: xS(db), y: yS(Math.min(ws, wMax)) });
  }
  pts.push({ x: xS(toDbF), y: yS(Math.min(wSat(toDbF), wMax)) });
  if (pts.length < 2) return [];
  return [{ type: 'curve', points: pts }];
}

const POINT_COLORS = {
  OA: '#EF4444', RA: '#22C55E', PA: '#2563EB', ERV_SA: '#F59E0B',
  PAC_OUT: '#2563EB', SPIN_OUT: '#059669', SA: '#DC2626',
  RA_W: '#0EA5E9', SA_HEAT: '#F97316', AO: '#2563EB',
  OP_AO: '#2563EB', ACB_AO: '#0891B2',
};
const POINT_SHORT = {
  OA: 'OA', RA: 'RA', PA: 'PA', ERV_SA: 'ERV', PAC_OUT: 'PAC Out', SPIN_OUT: 'Spin Out', SA: 'SA',
  RA_W: 'RA(W)', SA_HEAT: 'SA(H)', AO: 'LAT (Air Off)',
  OP_AO: 'Op Off', ACB_AO: 'ACB Off',
};
const LABEL_OFF = {
  OA: { dx: 8, dy: -14 }, RA: { dx: 10, dy: -14 }, PA: { dx: 10, dy: -14 },
  ERV_SA: { dx: 6, dy: -12 }, PAC_OUT: { dx: -52, dy: 16 }, SPIN_OUT: { dx: 6, dy: 16 },
  SA: { dx: 0, dy: 22, anchor: "middle" }, RA_W: { dx: 6, dy: 16 }, SA_HEAT: { dx: 6, dy: -12 },
  AO: { dx: -10, dy: -14, anchor: "end" }, OP_AO: { dx: 6, dy: 16 }, ACB_AO: { dx: 6, dy: -12 },
};

const COOLING_LABELS = ['PAC Coil', 'Spinnaker Coil', 'Cooling (Room \u2192 Supply)', '1. Secondary Air Cooling', '1. PA Cooling (Model Op)', '2. SA Cooling (ACB Coil)'];

export const PsychrometricChart = ({ data, isSI = false }) => {
  const { points, processes, season, capacity } = data || {};

  const bounds = useMemo(() => {
    if (!points || Object.keys(points).length === 0) return { dbMin: 40, dbMax: 100, wMin: 0, wMax: 0.020 };
    return computeBounds(points);
  }, [points]);

  const xS = useMemo(() => makeXScale(bounds.dbMin, bounds.dbMax), [bounds]);
  const yS = useMemo(() => makeYScale(bounds.wMin, bounds.wMax), [bounds]);

  const satPath = useMemo(() => pointsToPath(genSatCurve(xS, yS, bounds.dbMin, bounds.dbMax, bounds.wMax)), [xS, yS, bounds]);
  const rh80 = useMemo(() => pointsToPath(genRhCurve(80, xS, yS, bounds.dbMin, bounds.dbMax, bounds.wMax)), [xS, yS, bounds]);
  const rh60 = useMemo(() => pointsToPath(genRhCurve(60, xS, yS, bounds.dbMin, bounds.dbMax, bounds.wMax)), [xS, yS, bounds]);
  const rh40 = useMemo(() => pointsToPath(genRhCurve(40, xS, yS, bounds.dbMin, bounds.dbMax, bounds.wMax)), [xS, yS, bounds]);
  const rh20 = useMemo(() => pointsToPath(genRhCurve(20, xS, yS, bounds.dbMin, bounds.dbMax, bounds.wMax)), [xS, yS, bounds]);

  if (!points || Object.keys(points).length === 0) return null;

  // Generate ticks
  const dbStep = (bounds.dbMax - bounds.dbMin) <= 40 ? 5 : 10;
  const dbTicks = [];
  for (let db = bounds.dbMin; db <= bounds.dbMax; db += dbStep) dbTicks.push(db);

  const grainMax = Math.round(bounds.wMax * 7000);
  const grainStep = grainMax <= 60 ? 5 : grainMax <= 120 ? 10 : 20;
  const wTicks = [];
  for (let g = 0; g <= grainMax; g += grainStep) wTicks.push(g / 7000);

  return (
    <div className="border rounded overflow-hidden" data-testid="psych-chart">
      <div className={`px-3 py-2 font-bold text-sm ${season === 'winter' ? 'text-red-600' : 'text-blue-700'}`}>
        Psychrometric Process Chart ({season === 'winter' ? 'Heating' : 'Cooling'})
      </div>
      <div className="bg-card p-2 overflow-x-auto">
        <svg width={CHART_W} height={CHART_H} viewBox={`0 0 ${CHART_W} ${CHART_H}`} className="mx-auto">
          <rect x={MARGIN.left} y={MARGIN.top} width={PLOT_W} height={PLOT_H} fill="#FAFAFA" stroke="#E2E8F0" />

          {/* DB grid + ticks */}
          {dbTicks.map(db => (
            <g key={`db-${db}`}>
              <line x1={xS(db)} y1={MARGIN.top} x2={xS(db)} y2={MARGIN.top + PLOT_H} stroke="#E2E8F0" strokeWidth="0.5" />
              <line x1={xS(db)} y1={MARGIN.top + PLOT_H} x2={xS(db)} y2={MARGIN.top + PLOT_H + 5} stroke="#64748B" strokeWidth="1" />
              <text x={xS(db)} y={MARGIN.top + PLOT_H + 20} textAnchor="middle" fontSize="12" fill="#1E293B" fontWeight="600">{fmtT(db, isSI)}</text>
            </g>
          ))}

          {/* W grid + ticks */}
          {wTicks.map(w => (
            <g key={`w-${w}`}>
              <line x1={MARGIN.left} y1={yS(w)} x2={MARGIN.left + PLOT_W} y2={yS(w)} stroke="#E2E8F0" strokeWidth="0.5" />
              <line x1={MARGIN.left + PLOT_W} y1={yS(w)} x2={MARGIN.left + PLOT_W + 5} y2={yS(w)} stroke="#64748B" strokeWidth="1" />
              {season !== 'winter' && (
                <text x={MARGIN.left + PLOT_W + 8} y={yS(w) + 4} fontSize="11" fill="#1E293B" fontWeight="600">{fmtW(Math.round(w * 7000), isSI)}</text>
              )}
            </g>
          ))}

          {/* RH curves */}
          <path d={rh20} fill="none" stroke="#CBD5E1" strokeWidth="0.5" strokeDasharray="3,3" />
          <path d={rh40} fill="none" stroke="#CBD5E1" strokeWidth="0.5" strokeDasharray="3,3" />
          <path d={rh60} fill="none" stroke="#94A3B8" strokeWidth="0.7" strokeDasharray="3,3" />
          <path d={rh80} fill="none" stroke="#94A3B8" strokeWidth="0.7" strokeDasharray="3,3" />

          {/* RH labels near visible area */}
          {[20, 40, 60, 80].map(rh => {
            const labelDb = Math.min(bounds.dbMax - 5, (bounds.dbMin + bounds.dbMax) * 0.7);
            const w = wFromDbRh(labelDb, rh);
            if (w > bounds.wMax || w < bounds.wMin) return null;
            return <text key={`rhlbl-${rh}`} x={xS(labelDb) + 2} y={yS(w) - 3} fontSize="8" fill="#94A3B8">{rh}%</text>;
          })}

          {/* Saturation curve */}
          <path d={satPath} fill="none" stroke="#0F172A" strokeWidth="1.5" />

          {/* Arrow marker */}
          <defs>
            <marker id="arrow" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
              <path d="M0,0 L6,2 L0,4 Z" fill="#334155" />
            </marker>
          </defs>

          {/* Process lines */}
          {processes && processes.map((proc, i) => {
            const fromPt = points[proc.from];
            const toPt = points[proc.to];
            if (!fromPt || !toPt) return null;

            // Convert w_grains to w (lb/lb) if w is not provided
            const fromW = fromPt.w !== undefined ? fromPt.w : (fromPt.w_grains / 7000);
            const toW = toPt.w !== undefined ? toPt.w : (toPt.w_grains / 7000);

            const isCooling = (proc.cooling_path || COOLING_LABELS.includes(proc.label)) && fromPt.db_f > toPt.db_f;

            if (isCooling && !proc.dashed) {
              const segments = buildCoolingPath(fromPt.db_f, fromW, toPt.db_f, xS, yS, bounds.wMax);
              return (
                <g key={`proc-${i}`}>
                  {segments.map((seg, si) => {
                    if (seg.type === 'line') {
                      return <line key={si} x1={seg.x1} y1={seg.y1} x2={seg.x2} y2={seg.y2} stroke={proc.color} strokeWidth={2} />;
                    }
                    if (seg.type === 'curve' && seg.points.length > 1) {
                      const d = 'M ' + seg.points.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' L ');
                      return <path key={si} d={d} fill="none" stroke={proc.color} strokeWidth={2} markerEnd="url(#arrow)" />;
                    }
                    return null;
                  })}
                </g>
              );
            }

            return (
              <g key={`proc-${i}`}>
                <line
                  x1={xS(fromPt.db_f)} y1={yS(fromW)}
                  x2={xS(toPt.db_f)} y2={yS(toW)}
                  stroke={proc.color || '#333'}
                  strokeWidth={proc.width != null ? proc.width : (proc.dashed ? 1 : 2)}
                  strokeDasharray={proc.dashed ? '4,3' : 'none'}
                  markerEnd="url(#arrow)"
                />
              </g>
            );
          })}

          {/* State points */}
          {Object.entries(points).map(([key, pt]) => {
            const cx = xS(pt.db_f);
            // Convert w_grains to w (lb/lb) if w is not provided
            const w = pt.w !== undefined ? pt.w : (pt.w_grains / 7000);
            const cy = yS(w);
            const color = POINT_COLORS[key] || '#333';
            const off = LABEL_OFF[key] || { dx: 6, dy: -12 };
            return (
              <g key={key}>
                <circle cx={cx} cy={cy} r={5} fill={color} stroke="white" strokeWidth="1.5" />
                <text x={cx + off.dx} y={cy + off.dy} fontSize="11" fontWeight="700" fill={color} textAnchor={off.anchor || "start"}
                  stroke="white" strokeWidth="3" paintOrder="stroke">
                  {POINT_SHORT[key] || key}
                </text>
                <text x={cx + off.dx} y={cy + off.dy + 12} fontSize="9" fill="#475569" fontWeight="500" textAnchor={off.anchor || "start"}
                  stroke="white" strokeWidth="3" paintOrder="stroke">
                  {`${fmtT(pt.db_f, isSI, 1)}${isSI ? "°C" : "°F"} / ${rhFromDbW(pt.db_f, w).toFixed(0)}% RH`}
                </text>
              </g>
            );
          })}

          {/* Axis labels */}
          <text x={MARGIN.left + PLOT_W / 2} y={CHART_H - 2} textAnchor="middle" fontSize="12" fill="#0F172A" fontWeight="700">
            {`Dry Bulb Temperature (${isSI ? "°C" : "°F"})`}
          </text>
          <text x={12} y={MARGIN.top + PLOT_H / 2} textAnchor="middle" fontSize="12" fill="#0F172A" fontWeight="700"
            transform={`rotate(-90, 12, ${MARGIN.top + PLOT_H / 2})`}>
            {`Moisture (${isSI ? "g/kg dry air" : "grains/lb"})`}
          </text>
        </svg>

        {/* Legend — show every distinct process line once. We dedupe by label
            so the secondary AO→SA mixing leg (rendered with an empty label)
            doesn't clutter the row. */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', padding: '8px 12px', borderTop: '1px solid #e2e8f0', marginTop: '4px', fontSize: '11px' }}>
          {processes && (() => {
            const seen = new Set();
            const items = [];
            processes.forEach((proc) => {
              if (!proc.label || seen.has(proc.label)) return;
              seen.add(proc.label);
              items.push(proc);
            });
            return items.map((proc, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '24px', height: proc.width === 1.5 ? '2px' : '3px', borderRadius: '2px', backgroundColor: proc.color }} />
                <span style={{ color: '#475569' }}>{proc.label}</span>
              </div>
            ));
          })()}
        </div>

        {/* Delivered Capacity to Space */}
        {capacity && (
          <div className="border-t border-border bg-muted px-3 py-2" data-testid="psych-chart-capacity">
            <div className="text-xs font-semibold text-foreground mb-1.5">Delivered Capacity to Space</div>
            <div className="grid grid-cols-4 gap-3 text-[11px]">
              <div>
                <span className="text-muted-foreground">Total: </span>
                <span className="font-bold text-blue-700">{fmtCap(capacity.total_btuh, isSI)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Sensible: </span>
                <span className="font-semibold">{fmtCap(capacity.sensible_btuh, isSI)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Latent: </span>
                <span className="font-semibold">{fmtCap(capacity.latent_btuh, isSI)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Airflow: </span>
                <span className="font-semibold">{fmtFlow(capacity.total_airflow_cfm, isSI)}</span>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3 text-[11px] mt-1">
              <div>
                <span className="text-muted-foreground">Supply Air: </span>
                <span className="font-semibold">{fmtT(capacity.supply_db_f, isSI, 1)}{isSI ? "°C" : "°F"} / {capacity.supply_rh}% RH</span>
              </div>
              <div>
                <span className="text-muted-foreground">Room: </span>
                <span className="font-semibold">{fmtT(capacity.room_db_f, isSI, 1)}{isSI ? "°C" : "°F"} / {capacity.room_rh}% RH</span>
              </div>
              <div colSpan="2">
                <span className="text-muted-foreground">SHR: </span>
                <span className="font-semibold">{capacity.total_btuh > 0 ? (capacity.sensible_btuh / capacity.total_btuh * 100).toFixed(1) : '—'}%</span>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default PsychrometricChart;
